"use client";

import React from "react";
import Link from "next/link";
import { S3Image } from "@/components/common/S3Image";
import type {
  ContentWithImageSectionData,
  SectionProps,
} from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";

/**
 * Crystal Content-with-Image — text paired with an image in a frosted
 * glass frame, side by side. Restyle of the default section for the
 * crystal-night palette: glass image card, prism-underlined heading,
 * muted body, glass/solid CTA.
 */
export function ContentWithImageSection({
  section,
  currentLanguage = "en",
}: SectionProps<ContentWithImageSectionData>) {
  const { content, layout = "imageLeft" } = section;

  const title = content?.title
    ? getLocalizedText(content.title, currentLanguage)
    : "";
  // Tolerate nested (wizard/canonical) and flat (CMS editor) shapes.
  const descObj = (content as any)?.description ?? content;
  const imgUrl = (content as any)?.image?.url ?? (section as any).image ?? "";
  const imgAltObj = (content as any)?.image?.alt ?? (section as any).imageAlt;
  const description = descObj ? getLocalizedText(descObj, currentLanguage) : "";
  const imageAlt = imgAltObj
    ? getLocalizedText(imgAltObj, currentLanguage)
    : "Image";
  const button = (content as any)?.button ?? (section as any).button;
  const pos =
    section.imagePosition ?? (layout === "imageRight" ? "right" : "left");
  const isStacked = pos === "top" || pos === "bottom";
  const imgAlignClass =
    (section.imageAlign ?? "center") === "left"
      ? "mr-auto"
      : (section.imageAlign ?? "center") === "right"
        ? "ml-auto"
        : "mx-auto";

  return (
    <section
      className="py-16 md:py-24 px-4"
      data-section-id={section._id}
      data-section-type={section.type}
    >
      <div className="max-w-6xl mx-auto">
        <div
          className={`grid gap-10 lg:gap-14 items-center ${
            isStacked ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"
          }`}
        >
          {/* Image — frosted glass frame with a faint prism edge. */}
          {imgUrl && (
            <div
              className={
                isStacked
                  ? `w-full max-w-3xl ${imgAlignClass} ${
                      pos === "bottom" ? "order-2" : "order-1"
                    }`
                  : pos === "right"
                    ? "lg:order-2"
                    : ""
              }
            >
              <div className="crystal-glass relative overflow-hidden rounded-2xl p-2">
                <S3Image
                  src={imgUrl}
                  alt={imageAlt}
                  className="w-full h-auto object-cover rounded-xl"
                  width={640}
                  height={440}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
                <div
                  className="absolute inset-0 rounded-xl pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 55%, rgba(7,12,23,0.35) 100%)",
                  }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          <div
            className={`space-y-6 ${
              isStacked
                ? pos === "bottom"
                  ? "order-1"
                  : "order-2"
                : pos === "right"
                  ? "lg:order-1"
                  : ""
            }`}
          >
            {title && (
              <h2
                className="crystal-display text-3xl md:text-4xl"
                style={{ color: "var(--color-foreground)", textWrap: "balance" }}
              >
                {title}
              </h2>
            )}
            <span
              className="block h-1 w-14 rounded-full"
              style={{ background: "var(--gradient-primary)" }}
              aria-hidden="true"
            />
            {description && (
              <p
                className="text-base md:text-lg leading-relaxed"
                style={{ color: "var(--color-muted-foreground)" }}
              >
                {description}
              </p>
            )}
            {button?.url && (
              <Link
                href={button.url}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm md:text-base font-semibold transition-transform hover:-translate-y-0.5"
                style={{
                  backgroundColor: "var(--color-primary)",
                  color: "var(--color-primary-foreground)",
                }}
              >
                {getLocalizedText(button.text, currentLanguage)}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContentWithImageSection;
